import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Centralised DataGrid look-and-feel. Apply via the `sx` prop on any
 * `<DataGrid>` so every table in the app shares the same polish.
 */
export const dataGridSx: SxProps<Theme> = {
  border: 'none',
  fontFamily: 'inherit',
  fontSize: '0.85rem',
  color: '#0f172a',

  '& .MuiDataGrid-columnHeaders': {
    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
    borderBottom: '1px solid #e2e8f0',
    minHeight: '52px !important',
    maxHeight: '52px !important',
    lineHeight: '52px !important',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '0.72rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#475569',
  },
  '& .MuiDataGrid-columnSeparator': { display: 'none' },
  // Centre every column header label (overrides per-column headerAlign defaults
  // so the whole grid reads consistently centred).
  '& .MuiDataGrid-columnHeaderTitleContainer': { justifyContent: 'center' },
  '& .MuiDataGrid-columnHeaderTitleContainerContent': { justifyContent: 'center' },

  // Centre cell content both ways. The cell is already a flex container
  // (our justifyContent below takes effect), so we also pin the cross axis
  // with alignItems:center — this vertically centres multi-line renderCell
  // content (e.g. the App/Host icon + two-line label) that otherwise sits at
  // the top. We intentionally don't touch `display` (re-declaring it once
  // broke some renderCell flex-row stacks).
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid #f1f5f9',
    fontSize: '0.85rem',
    color: '#0f172a',
    outline: 'none !important',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  '& .MuiDataGrid-row': {
    transition: 'background-color 120ms ease, transform 120ms ease',
  },
  '& .MuiDataGrid-row:nth-of-type(even)': {
    backgroundColor: 'rgba(15, 23, 42, 0.015)',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(99, 102, 241, 0.06) !important',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: 'rgba(99, 102, 241, 0.1) !important',
  },
  '& .MuiDataGrid-row.Mui-selected:hover': {
    backgroundColor: 'rgba(99, 102, 241, 0.16) !important',
  },

  '& .MuiCheckbox-root.Mui-checked': { color: '#6366f1' },
  '& .MuiCheckbox-root.MuiCheckbox-indeterminate': { color: '#6366f1' },

  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    minHeight: 48,
  },
  '& .MuiTablePagination-root': { color: '#475569', fontWeight: 600 },
  '& .MuiTablePagination-selectIcon': { color: '#475569' },

  '& .MuiDataGrid-virtualScroller': { backgroundColor: '#fff' },
  '& .MuiDataGrid-overlay': { backgroundColor: 'rgba(255, 255, 255, 0.85)' },
  // Suppress the outline for mouse focus, but keep a visible cue for keyboard
  // users (:focus-visible) so grid navigation stays accessible.
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none !important',
  },
  '& .MuiDataGrid-columnHeader:focus-visible, & .MuiDataGrid-cell:focus-visible': {
    outline: '2px solid #6366f1 !important',
    outlineOffset: '-2px',
  },
};

export const dataGridContainerSx: SxProps<Theme> = {
  borderRadius: 3,
  overflow: 'hidden',
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
  backgroundColor: '#fff',
};
